import './assistant.css';
import type { ViewerShell } from '../../shell';
import { isCompactChrome } from '../../ui/compact';
import { confirmDialog, openDialog, paragraph } from '../../ui/dialogs';
import { RisError } from '../client';
import { hasPerm, risClient } from '../session';
import { type AssistantAction, AssistantApi, type AssistantFinding, type FindingStatus } from './api';
import { runAction, jumpTo } from './actions';
import { allFindings, drawnOn, finding as storedFinding, install as installOverlay, setFindings } from './findings-overlay';
import { type MessageHandlers, renderFinding } from './messages';
import { runSeriesAnalysis } from './analysis';
import { lastStream, type StreamTrace } from './live-answer';
import { type AssistantPanelHook, AssistantPanel } from './panel';
import { REPORT_TEXT, structuredReportCard } from './report-panel';
import { SIMILAR_TEXT, similarCasesCard } from './similar';
import { readDrawer, storeDrawer } from './drawer-memory';
import { addHideButton, installResize } from './drawer-size';
import { type DrawerTab, type DrawerTabs, installTabs } from './tabs';
import { installTools } from './tools';
import { installRadiologistTools, markFindingKeyImage, showFollowup, toolOn } from './radiologist';
import type { RadiologistDeps } from './radiologist/ui';

/**
 * The Asistente (viewer-ai contract §4): a second tab in the right drawer where the radiologist asks
 * about the image on screen, reviews the suggested findings (Aceptar / Corregir / Descartar through the
 * existing ris decision routes) and runs the viewer actions an answer proposes. It never signs and
 * never decides: findings stay suggestions until a person reviews them, and an accepted one becomes an
 * annotation only when the radiologist draws it. Shown only with ris and the `viewer:ai:run` permission.
 */

export const AI_PERM = { run: 'viewer:ai:run', review: 'viewer:ai:review' } as const;

export const REVIEW_TEXT = {
  correctTitle: 'Corregir hallazgo sugerido',
  correctLabel: 'Texto corregido',
  correctHint: 'Describa el hallazgo como debe quedar (mínimo 3 caracteres).',
  correctTooShort: 'Escriba al menos 3 caracteres.',
  save: 'Guardar corrección',
  cancel: 'Cancelar',
  accepted: 'Hallazgo aceptado. Para conservarlo como anotación, dibújelo con las herramientas.',
  conflict: 'Otra persona ya revisó este hallazgo.',
  undoForbidden: 'Solo quien revisó el hallazgo puede deshacerlo.',
  failed: 'No fue posible guardar la revisión del hallazgo.',
} as const;

export const ESCALATE_TEXT = {
  title: 'Escalar como hallazgo crítico',
  text: 'Se creará un hallazgo crítico en el módulo de hallazgos críticos, con su propio seguimiento y escalamiento. '
    + 'No se notifica al paciente. ¿Desea continuar?',
  confirm: 'Escalar',
  cancel: 'Cancelar',
  done: 'Hallazgo escalado al módulo de hallazgos críticos.',
  forbidden: 'No dispone de permisos para escalar hallazgos críticos.',
  notAccepted: 'Solo se puede escalar un hallazgo crítico aceptado o corregido.',
  failed: 'No fue posible escalar el hallazgo.',
} as const;

function escalateErrorText(error: unknown): string {
  if (error instanceof RisError && (error.status === 401 || error.status === 403)) return ESCALATE_TEXT.forbidden;
  if (error instanceof RisError && (error.status === 409 || error.status === 422)) return ESCALATE_TEXT.notAccepted;
  return ESCALATE_TEXT.failed;
}

const MIN_CORRECTION = 3;
const MAX_CORRECTION = 500;

function decisionErrorText(error: unknown, undo: boolean): string {
  if (error instanceof RisError && error.status === 409) return REVIEW_TEXT.conflict;
  if (error instanceof RisError && error.status === 403) return undo ? REVIEW_TEXT.undoForbidden : REVIEW_TEXT.failed;
  return REVIEW_TEXT.failed;
}

/** In-viewer dialog for the corrected text (never window.prompt); null when cancelled. */
export function askCorrection(shell: ViewerShell, current: AssistantFinding): Promise<string | null> {
  return new Promise((resolve) => {
    let answered = false;
    const textarea = document.createElement('textarea');
    textarea.className = 'assistant-correction-input';
    textarea.rows = 4;
    textarea.maxLength = MAX_CORRECTION;
    textarea.value = current.correction ?? current.label;
    textarea.id = 'assistant-correction-input';
    const error = document.createElement('p');
    error.className = 'assistant-correction-error';
    error.setAttribute('role', 'alert');
    const finish = (value: string | null, close: () => void) => {
      answered = true;
      close();
      resolve(value);
    };
    const { body, dialog } = openDialog(shell, {
      title: REVIEW_TEXT.correctTitle,
      className: 'assistant-correction-dialog',
      buttons: [
        { label: REVIEW_TEXT.cancel, onClick: (close) => finish(null, close) },
        {
          label: REVIEW_TEXT.save,
          primary: true,
          onClick: (close) => {
            const value = textarea.value.trim();
            if (value.length < MIN_CORRECTION) {
              error.textContent = REVIEW_TEXT.correctTooShort;
              textarea.focus();
              return;
            }
            finish(value, close);
          },
        },
      ],
    });
    const label = document.createElement('label');
    label.className = 'assistant-correction-label';
    label.htmlFor = textarea.id;
    label.textContent = REVIEW_TEXT.correctLabel;
    paragraph(body, REVIEW_TEXT.correctHint, 'assistant-correction-hint');
    body.append(label, textarea, error);
    textarea.focus();
    textarea.select();
    // Esc / backdrop close the dialog without an answer.
    const observer = new MutationObserver(() => {
      if (dialog.isConnected) return;
      observer.disconnect();
      if (!answered) resolve(null);
    });
    observer.observe(shell.regions.overlayLayer, { childList: true });
  });
}

class Assistant {
  readonly panel: AssistantPanel;
  private readonly handlers: MessageHandlers;
  private readonly escalated = new Set<string>();

  constructor(private readonly shell: ViewerShell, private readonly api: AssistantApi) {
    this.handlers = {
      canReview: hasPerm(AI_PERM.review),
      onJump: (f) => {
        if (f.location) jumpTo(shell, f.location);
      },
      onDecide: (f, decision) => void this.decide(f, decision),
      onUndo: (f) => void this.undo(f),
      onAction: (action: AssistantAction) => void runAction({ shell, api }, action),
      onEscalate: (f) => void this.escalate(f),
      isEscalated: (f) => this.escalated.has(f.id),
      onFollowup: (f) => void showFollowup(this.radiologist, f),
      onKeyImage: (f) => void markFindingKeyImage(this.radiologist, f),
      toolShown: (tool) => toolOn(tool),
    };
    this.panel = new AssistantPanel(shell, api, this.handlers);
    const render = (f: AssistantFinding) => renderFinding(f, this.handlers);
    this.panel.setSweepHandler(() => runSeriesAnalysis({ shell, api, panel: this.panel, renderFinding: render }));
    this.panel.addToolButton(REPORT_TEXT.tool, () => this.panel.appendResult(structuredReportCard(shell, api)));
    this.panel.addToolButton(SIMILAR_TEXT.tool, () => {
      const card = this.panel.appendResult(similarCasesCard(shell, api));
      card.querySelector('input')?.focus();
    });
  }

  /** What the radiologist tools (contract v3) get: the same panel, routes and finding handlers. */
  get radiologist(): RadiologistDeps {
    return { shell: this.shell, panel: this.panel, api: this.api, handlers: this.handlers };
  }

  /** "Escalar como crítico": confirmed in the viewer, then the critical-findings module takes over (idempotent in ris). */
  private async escalate(f: AssistantFinding): Promise<void> {
    const confirmed = await confirmDialog(this.shell, {
      title: ESCALATE_TEXT.title, text: `${f.label}\n\n${ESCALATE_TEXT.text}`,
      confirmLabel: ESCALATE_TEXT.confirm, cancelLabel: ESCALATE_TEXT.cancel, className: 'assistant-escalate-dialog',
    });
    if (!confirmed) return;
    try {
      await this.api.escalate(f.id);
      this.escalated.add(f.id);
      this.panel.replaceFinding(storedFinding(f.id) ?? f, (next) => renderFinding(next, this.handlers));
      this.shell.toast(ESCALATE_TEXT.done);
    } catch (error) {
      console.warn('[viewer] finding escalation failed', error);
      this.shell.toast(escalateErrorText(error), 'error');
    }
  }

  /** The server answers with the finding row (no location): keep what the message said about it. */
  private update(previous: AssistantFinding, next: AssistantFinding | null, fallback: FindingStatus): void {
    const merged: AssistantFinding = {
      ...(storedFinding(previous.id) ?? previous),
      status: next?.status ?? fallback,
      correction: next ? next.correction : previous.correction,
    };
    setFindings([merged]);
    this.panel.replaceFinding(merged, (f) => renderFinding(f, this.handlers));
  }

  private async decide(f: AssistantFinding, decision: Exclude<FindingStatus, 'pending'>): Promise<void> {
    let correction: string | undefined;
    if (decision === 'corrected') {
      const text = await askCorrection(this.shell, f);
      if (text === null) return;
      correction = text;
    }
    try {
      const out = await this.api.decide(f.id, decision, correction);
      this.update(f, out ? { ...out, correction: out.correction ?? correction ?? null } : null, decision);
      if (decision === 'accepted') this.shell.toast(REVIEW_TEXT.accepted);
    } catch (error) {
      console.warn('[viewer] finding decision failed', error);
      this.shell.toast(decisionErrorText(error, false), 'error');
    }
  }

  private async undo(f: AssistantFinding): Promise<void> {
    try {
      const out = await this.api.undo(f.id);
      this.update(f, out ?? null, 'pending');
    } catch (error) {
      console.warn('[viewer] finding undo failed', error);
      this.shell.toast(decisionErrorText(error, true), 'error');
    }
  }
}

interface AssistantDebug {
  findings(): AssistantFinding[];
  drawn(cell?: number): number;
  select(tab: DrawerTab): void;
  /** Deltas the last streamed answer rendered before its final message. */
  streamed(): StreamTrace;
  /** The tools hook (what VIEW-B's modules use). */
  panel(): AssistantPanelHook | null;
}

let installed: AssistantPanel | null = null;

/**
 * The Asistente's tools hook for other modules (VIEW-B: region, measure, lesions, commands): null
 * when the assistant is not installed (no ris or no viewer:ai:run). Call after `install`.
 */
export function assistantPanel(): AssistantPanelHook | null {
  return installed;
}

declare global {
  interface Window {
    __viewerAssistant?: AssistantDebug;
  }
}

const TOGGLE_TITLE = { open: 'Cerrar el Asistente Guru', closed: 'Abrir el Asistente Guru' } as const;

/**
 * "Asistente" in the top bar, before "Anotaciones": opens the right drawer straight on the Asistente tab
 * (a second click closes it). Answers the function that re-syncs its pressed state.
 */
function addToggleButton(shell: ViewerShell, tabs: DrawerTabs): () => void {
  const button = document.createElement('button');
  button.type = 'button';
  button.id = 'xv-assistant-toggle-btn';
  button.className = 'toolbar-button assistant-toggle';
  button.textContent = 'Asistente Guru';
  const showing = () => shell.isRightDrawerOpen() && tabs.current() === 'assistant';
  const sync = () => {
    storeDrawer({ open: shell.isRightDrawerOpen(), tab: tabs.current() });
    const open = showing();
    button.classList.toggle('active', open);
    button.setAttribute('aria-pressed', String(open));
    button.title = open ? TOGGLE_TITLE.open : TOGGLE_TITLE.closed;
  };
  button.addEventListener('click', () => {
    if (showing()) {
      shell.setRightDrawer(false);
    } else {
      tabs.select('assistant');
      shell.setRightDrawer(true);
    }
    sync();
  });
  // The Anotaciones button and the compact chrome also open and close the drawer: keep this one's state true.
  document.getElementById('xv-annotations-toggle-btn')?.addEventListener('click', sync);
  shell.on('drawer-changed', sync);
  const anchor = document.getElementById('xv-annotations-toggle-btn');
  if (anchor) anchor.before(button);
  else shell.regions.topNav.appendChild(button);
  sync();
  return sync;
}

/** Installed after src/ris (needs the session) and src/annotations (owns the drawer it adds a tab to). */
export function install(shell: ViewerShell): void {
  const client = risClient();
  if (!client || !hasPerm(AI_PERM.run)) return;
  const assistant = new Assistant(shell, new AssistantApi(client));
  installed = assistant.panel;
  installOverlay(shell);
  let syncToggle = (): void => undefined;
  const tabs = installTabs(shell.regions.rightDrawer, assistant.panel.root, (tab) => {
    assistant.panel.setVisible(tab === 'assistant');
    // The drawer is wider on the Asistente tab: let the viewports follow.
    if (shell.isRightDrawerOpen()) shell.setRightDrawer(true);
    syncToggle();
  });
  installResize(shell);
  addHideButton(assistant.panel.root, () => {
    shell.setRightDrawer(false);
    syncToggle();
  });
  // Read before the button's first sync, which stores the state the viewer starts in.
  const remembered = readDrawer();
  syncToggle = addToggleButton(shell, tabs);
  // A reload comes back to the Asistente when that is where the reader left it (not over a phone's image).
  if (remembered?.open && remembered.tab === 'assistant' && !isCompactChrome()) {
    tabs.select('assistant');
    shell.setRightDrawer(true);
    syncToggle();
  }
  installTools(shell, assistant.panel, client, assistant['handlers']); // VIEW-B tools: region, measure, lesions, voice commands
  void installRadiologistTools(assistant.radiologist); // round 3: the tools the org switched on (GET /ai/tools)
  window.__viewerAssistant = {
    findings: allFindings,
    drawn: (cell = 0) => drawnOn(cell),
    select: (tab) => tabs.select(tab),
    streamed: lastStream,
    panel: assistantPanel,
  };
}
